/*
 * ContractForm.tsx : Un formulaire pour ajouter ou modifier un contrat.
 */

// Import React
import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';

// Import FontAwesome
import {
	faClock,
	faCoins,
	faInfoCircle,
	faRepeat,
} from '@fortawesome/free-solid-svg-icons';

// Import Communs et Layout
import {
	DatePickerField,
	finishFormSave,
	FormSaveActions,
	InputField,
	SelectField,
	useFormDirty,
} from '../../../common';
import CardHeader from '../../../layout/CardHeader';
import CardSection from '../../../layout/CardSection';

// Import Contract
import { ContractData } from '../types/types';
import { contractsApi } from '../services/contractsApi';

// Import Centre de coûts
import { costCentersApi } from '../../costCenter/services/costCentersApi';

// Import Centre de coûts - Catégories
import { costCenterCategoriesApi } from '../../costCenterCategories/services/costCenterCategoriesApi';

// Import Tiers
import { tiersApi } from '../../tiers/services/tiersApi';

// Composant ContractForm
const ContractForm: React.FC = () => {
	// Hooks de State
	const [loading, setLoading] = React.useState(true);
	const [baselineReady, setBaselineReady] = React.useState(false);
	const [formData, setFormData] = React.useState<ContractData>({
		id: 0,
		label: '',
		supplier: { id: 0, label: '' },
		costCenter: { id: 0, label: '' },
		periodicity: 'MONTHLY',
		estimatedAmountHt: 0,
		startDate: new Date(),
		endDate: null,
		active: true,
	});

	// Hook du select des centres des coûts
	const [costCenterOptions, setCostCenterOptions] = React.useState<
		{ key: string; label: string }[]
	>([]);

	// Hook du select des catégories de centres de coûts
	const [costCenterCategoryOptions, setCostCenterCategoryOptions] =
		React.useState<{ key: string; label: string }[]>([]);

	// Hook du select des fournisseurs (tiers)
	const [supplierOptions, setSupplierOptions] = React.useState<
		{ key: string; label: string }[]
	>([]);
	const [periodicityOptions] = React.useState<{ key: string; label: string }[]>(
		[
			{ key: 'MONTHLY', label: 'Mensuel' },
			{ key: 'QUARTERLY', label: 'Trimestriel' },
			{ key: 'YEARLY', label: 'Annuel' },
		]
	);

	// Hook de Navigation et Paramètres de navigation
	const navigate = useNavigate();
	const { id } = useParams<{ id: string }>();
	const { isDirty, markClean } = useFormDirty(formData, baselineReady);

	// Une fois le composant monté et si un id est présent, fetch des données et préremplissage du formulaire
	React.useEffect(() => {
		// Vérifier la présence du token d'authentification
		const token = localStorage.getItem('token');
		if (!token) return;

		// On commence par charger les catégories de centres de coûts
		costCenterCategoriesApi
			.listSelect(token)
			.then((items) => {
				const options = items.map((item) => ({
					key: item.id.toString(),
					label: String(item.label),
				}));
				setCostCenterCategoryOptions(options);
			})
			.catch((error) => console.error(error));

		// puis les tiers (fournisseurs)
		tiersApi
			.listPaginated(token, { limit: 999999 })
			.then((res) => {
				const options = res.data.map((tier) => ({
					key: tier.id.toString(),
					label: tier.label,
				}));
				setSupplierOptions(options);
			})
			.catch((error) => console.error(error));

		// Si un id est présent, fetch des données du contrat à éditer

		if (id) {
			contractsApi
				.get(Number(id), token)
				.then((data) => {
					setFormData(data);
					// filtrer les centres de coûts en utilisant directement `data`
					data.costCenter
						? costCentersApi
								.listPaginated(token, { limit: 999999 })
								.then((centers) =>
									centers.data
										.filter(
											(center) =>
												center.categorie?.id === data.costCenter?.categorie?.id
										)
										.map((center) => ({
											key: center.id.toString(),
											label: center.label,
										}))
								)
								.then(setCostCenterOptions)
						: setCostCenterOptions([]);
				})
				.catch((error) => console.error(error))
				.finally(() => {
					setLoading(false);
					setBaselineReady(true);
				});
		} else {
			// Si pas d'id, on arrête le loading directement
			setLoading(false);
			setBaselineReady(true);
		}
	}, [id]);

	// Gestion des changements dans le formulaire
	const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const { name, value } = e.target;
		setFormData({ ...formData, [name]: value });
	};

	// Gestion des changements dans le formulaire pour les selects
	const handleChangeSelect = (e: React.ChangeEvent<HTMLSelectElement>) => {
		const { name, value } = e.target;

		if (name === 'costCenterCategory') {
			const selectedOption = costCenterCategoryOptions.find(
				(opt) => opt.key === value
			);
			if (!selectedOption) return;

			// Récupérer les centres de coûts filtrés par catégorie
			costCentersApi
				.listPaginated(localStorage.getItem('token') || '', { limit: 999999 })
				.then((res) => {
					const options = res.data
						.filter(
							(center) => center.categorie?.id === Number(selectedOption.key)
						)
						.map((center) => ({
							key: center.id.toString(),
							label: center.label,
						}));

					setCostCenterOptions(options);

					// Sélectionner le premier centre filtré si disponible
					if (options.length > 0) {
						setFormData((prev) => ({
							...prev,
							costCenter: {
								id: Number(options[0].key),
								label: options[0].label,
								categorie: {
									id: Number(selectedOption.key),
									label: selectedOption.label,
								},
							},
						}));
					} else {
						setFormData((prev) => ({ ...prev, costCenter: null }));
					}
				})
				.catch(console.error);

			return;
		}

		if (name === 'costCenter') {
			const selectedOption = costCenterOptions.find((opt) => opt.key === value);
			if (selectedOption) {
				setFormData((prev) => ({
					...prev,
					costCenter: {
						id: Number(selectedOption.key),
						label: selectedOption.label,
						categorie: prev.costCenter?.categorie || null,
					},
				}));
			}
			return;
		}

		if (name === 'supplier') {
			const selectedOption = supplierOptions.find((opt) => opt.key === value);
			if (selectedOption) {
				setFormData((prev) => ({
					...prev,
					supplier: {
						id: Number(selectedOption.key),
						label: selectedOption.label,
					},
				}));
			}
			return;
		}

		if (name === 'periodicity') {
			setFormData((prev) => ({
				...prev,
				periodicity: value,
			}));
			return;
		}

		// Mise à jour standard pour les autres champs
		setFormData((prev) => ({ ...prev, [name]: value }));
	};

	// Gestion de la soumission du formulaire
	const [isSubmitting, setIsSubmitting] = React.useState(false);
	const handleSave = (shouldLeave: boolean) => {
		// Vérifier la présence du token d'authentification
		const token = localStorage.getItem('token');
		if (!token) return;
		// Indiquer que la soumission est en cours
		setIsSubmitting(true);
		const onLeave = () => navigate('/app/contracts');
		// Si un id est présent, mise à jour de l'élément existant
		if (id) {
			contractsApi
				.update(Number(id), formData, token)
				.then(() => finishFormSave(shouldLeave, { onLeave, markClean }))
				.catch((error) => console.error(error))
				.finally(() => setIsSubmitting(false));
		} else {
			// Sinon, création d'un nouvel élément
			contractsApi
				.create(formData, token)
				.then((created) =>
					finishFormSave(shouldLeave, {
						onLeave,
						markClean,
						stayOnEdit: created?.id
							? () =>
									navigate(`/app/contracts/${created.id}/edit`, {
										replace: true,
									})
							: undefined,
					})
				)
				.catch((error) => console.error(error))
				.finally(() => setIsSubmitting(false));
		}
	};

	const handleSubmit = (e: React.FormEvent) => {
		// Empêcher le comportement par défaut du formulaire
		e.preventDefault();
		handleSave(true);
	};

	// Rendu du composant, le premier div est la card principale
	return (
		<div className="max-w-6xl mx-auto p-6 bg-white rounded-lg shadow-md space-y-6">
			{/* HEADER DE LA CARD */}
			<CardHeader
				icon={faRepeat}
				title={id ? "Édition d'un contrat" : 'Nouveau contrat'}
				isActive={formData.active ? true : false}
				onToggle={(v) => setFormData({ ...formData, active: v })}
				createdAt={
					formData.createdAt ? formData.createdAt?.toLocaleString() : undefined
				}
				loading={loading}
			/>

			{/* CONTENU DE LA CARD */}
			<form onSubmit={handleSubmit} className="space-y-6">
				{/* 2 COLONNES */}
				<div className="grid grid-cols-1 md:grid-cols-2 gap-6">
					{/* COLONNE GAUCHE */}
					<CardSection icon={faInfoCircle} title="Informations générales">
						<InputField
							label="Label"
							name="label"
							value={formData.label}
							onChange={handleChange}
							required
							loading={loading}
							placeholder='ex: "Annuités d&apos;amortissements Couvercle Cuve"'
						/>
						<SelectField
							label="Fournisseur"
							selectedKey={formData.supplier?.id.toString() || ''}
							onValueChange={(val) =>
								handleChangeSelect({
									target: {
										name: 'supplier',
										value: val,
									},
								} as React.ChangeEvent<HTMLSelectElement>)
							}
							options={supplierOptions}
							loading={loading}
							entityHref={
								formData.supplier?.id
									? `/app/tiers/${formData.supplier.id}/edit`
									: undefined
							}
							onClear={() =>
								setFormData((prev) => ({
									...prev,
									supplier: null,
								}))
							}
						/>
					</CardSection>

					{/* COLONNE DROITE */}
					<CardSection icon={faClock} title="Dates et périodicité">
						<SelectField
							label="Périodicité"
							selectedKey={formData.periodicity || ''}
							onValueChange={(val) =>
								handleChangeSelect({
									target: {
										name: 'periodicity',
										value: val,
									},
								} as React.ChangeEvent<HTMLSelectElement>)
							}
							options={periodicityOptions}
							loading={loading}
						/>
						<DatePickerField
							label="Date de début"
							name="startDate"
							value={
								formData.startDate ? new Date(formData.startDate) : undefined
							}
							onChange={(date) => setFormData({ ...formData, startDate: date })}
							required
							loading={loading}
						/>
						<DatePickerField
							label="Date de fin"
							name="endDate"
							value={formData.endDate ? new Date(formData.endDate) : undefined}
							onChange={(date) => setFormData({ ...formData, endDate: date })}
							loading={loading}
						/>
					</CardSection>
				</div>

				{/* COLONNE BASSE */}
				<CardSection icon={faCoins} title="Budget" className="mt-6">
					<div className="grid grid-cols-1 md:grid-cols-2 gap-16">
						<SelectField
							label="Catégorie de centre de coûts"
							selectedKey={formData.costCenter?.categorie?.id.toString() || ''}
							onValueChange={(val) =>
								handleChangeSelect({
									target: {
										name: 'costCenterCategory',
										value: val,
									},
								} as React.ChangeEvent<HTMLSelectElement>)
							}
							options={costCenterCategoryOptions}
							loading={loading}
							entityHref={
								formData.costCenter?.categorie?.id
									? `/app/settings/cost-center-categories/${formData.costCenter.categorie.id}/edit`
									: undefined
							}
							onClear={() =>
								setFormData((prev) => ({
									...prev,
									costCenter: null,
								}))
							}
						/>
						<SelectField
							label="Centre de coûts"
							selectedKey={formData.costCenter?.id.toString() || ''}
							onValueChange={(val) =>
								handleChangeSelect({
									target: {
										name: 'costCenter',
										value: val,
									},
								} as React.ChangeEvent<HTMLSelectElement>)
							}
							options={costCenterOptions}
							loading={loading}
							entityHref={
								formData.costCenter?.id
									? `/app/settings/cost-centers/${formData.costCenter.id}/edit`
									: undefined
							}
							onClear={() =>
								setFormData((prev) => ({
									...prev,
									costCenter: null,
								}))
							}
						/>
					</div>
					<div className="grid grid-cols-1 md:grid-cols-2 gap-28">
						<InputField
							label="Montant estimé HT"
							name="estimatedAmountHt"
							type="number"
							value={formData.estimatedAmountHt || 0}
							onChange={handleChange}
							required
							loading={loading}
							placeholder='ex: "0.0"'
						/>
					</div>
				</CardSection>
			</form>

			<FormSaveActions
				isDirty={isDirty}
				isSubmitting={isSubmitting}
				onCancel={() => navigate('/app/contracts')}
				onSave={() => handleSave(false)}
				onSaveAndLeave={() => handleSave(true)}
			/>
		</div>
	);
};

export default ContractForm;
